/** Dhara = rule for how many days interest is charged (matches backend utils/dharaInterest.js). */

export const DEFAULT_DHARA_LEGAL = '30_30';

function slabDhara(actualDays, firstMin) {
  if (actualDays <= firstMin) return firstMin;
  let charged = firstMin;
  let remaining = actualDays - firstMin;
  while (remaining > 0) {
    const chunk = Math.min(remaining, 30);
    charged += chunk <= 15 ? 15 : 30;
    remaining -= chunk;
  }
  return charged;
}

export function computeChargedDays(actualDays, dhara) {
  const days = Math.max(0, Math.floor(Number(actualDays) || 0));
  if (days === 0) return 0;

  switch (dhara) {
    case '30_30':
      return Math.ceil(days / 30) * 30;
    case '30_15':
      return slabDhara(days, 30);
    case '15_15':
      return slabDhara(days, 15);
    case '30_day':
      return days <= 30 ? 30 : 30 + (days - 30);
    case '15_day':
      return days <= 15 ? 15 : 15 + (days - 15);
    default:
      return days <= 30 ? 30 : 30 + (days - 30);
  }
}

export function computeInterest(principal, ratePercent, actualDays, dhara) {
  const chargedDays = computeChargedDays(actualDays, dhara);
  const daily = (Number(principal) * Number(ratePercent) / 100) / 360;
  return {
    interest: Math.round(daily * chargedDays),
    chargedDays,
    actualDays: Math.max(0, Math.floor(Number(actualDays) || 0))
  };
}

/** Yearly compound after more than one 360-day year. Null means keep simple interest. */
export function yearlyCompoundRelease(principal, ratePercent, actualDays) {
  const P = Number(principal) || 0;
  const rate = Number(ratePercent) || 0;
  const days = Math.max(0, Math.floor(Number(actualDays) || 0));
  if (!(P > 0) || !(rate > 0) || days <= 360) return null;
  const fullYears = Math.floor(days / 360);
  const leftoverDays = days - fullYears * 360;
  const grown = P * Math.pow(1 + rate / 100, fullYears);
  const totalRaw = grown * (1 + (rate / 100) * (leftoverDays / 360));
  const total = Math.round(totalRaw);
  const interest = Math.round(total - P);
  return { interest, total, fullYears, leftoverDays, actualDays: days };
}

export function calendarDaysBetween(startDate, endDate) {
  const s = startDate instanceof Date ? startDate : new Date(startDate);
  const e = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  return Math.max(0, Math.ceil((e - s) / (1000 * 60 * 60 * 24)));
}

/** Phone-app / 30-360 day count: 9 Oct → 30 Nov = 1 month 21 days = 51. */
export function days360(startDate, endDate) {
  const s = startDate instanceof Date ? startDate : new Date(startDate);
  const e = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const y1 = s.getFullYear();
  const m1 = s.getMonth() + 1;
  const d1 = s.getDate();
  const y2 = e.getFullYear();
  const m2 = e.getMonth() + 1;
  const d2 = e.getDate();
  return Math.max(0, 360 * (y2 - y1) + 30 * (m2 - m1) + (d2 - d1));
}

export function resolvePledgeDhara(pledge, defaultDhara = DEFAULT_DHARA_LEGAL) {
  return pledge?.customer?.dhara || pledge?.loanDetails?.dhara || defaultDhara;
}

/** Interest from pledge date to today (or release date if already closed). Uses ledger/pledge dhara. */
export function interestTillDate(pledge, asOf = new Date(), defaultDhara = DEFAULT_DHARA_LEGAL) {
  const principal = Number(pledge?.loanDetails?.principalAmount) || 0;
  const rate = Number(pledge?.loanDetails?.rateOfInterest) || 0;
  const start = pledge?.date;
  if (!principal || !rate || !start) return 0;
  const status = String(pledge?.status || '').toUpperCase();
  const closedAt = pledge?.releaseDate || pledge?.releasedAt || pledge?.closedDate || pledge?.closedAt;
  const end =
    (status === 'RELEASED' || status === 'CLOSED') && closedAt ? closedAt : asOf;
  const actualDays = days360(start, end);
  const dhara = resolvePledgeDhara(pledge, defaultDhara);
  return computeInterest(principal, rate, actualDays, dhara).interest;
}
